"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import type { CarouselDotsProps } from "./types";

/**
 * Carousel pagination dots component
 */
export const CarouselDots = React.forwardRef<HTMLDivElement, CarouselDotsProps>(
  (
    {
      total,
      current,
      onDotClick,
      className,
      dotClassName,
      activeDotClassName,
      variant = "default",
    },
    ref
  ) => {
    if (total <= 1) return null;

    const variantStyles = {
      default: {
        container: "flex justify-center gap-1.5",
        dot: "size-2 rounded-full bg-white/30 transition-all duration-300 cursor-pointer hover:bg-white/50",
        active: "bg-white",
      },
      line: {
        container: "flex justify-center gap-1",
        dot: "h-0.5 flex-1 bg-white/30 rounded-full overflow-hidden transition-all duration-300",
        active: "bg-white",
      },
      pill: {
        container: "flex justify-center gap-1.5",
        dot: "h-2 w-2 rounded-full bg-gray-300 transition-all duration-300 cursor-pointer",
        active: "w-[22px] !rounded-full bg-primary-500 !opacity-100",
      },
    };

    const styles = variantStyles[variant];

    return (
      <div ref={ref} className={cn(styles.container, className)} role="tablist">
        {Array.from({ length: total }).map((_, index) => (
          <button
            key={index}
            role="tab"
            aria-selected={index === current}
            aria-label={`Go to slide ${index + 1}`}
            className={cn(
              styles.dot,
              dotClassName,
              index === current && cn(styles.active, activeDotClassName)
            )}
            onClick={() => onDotClick?.(index)}
          />
        ))}
      </div>
    );
  }
);

CarouselDots.displayName = "CarouselDots";

/**
 * Pagination counter (e.g., "3 / 10")
 */
export interface PaginationCounterProps {
  current: number;
  total: number;
  className?: string;
}

export const PaginationCounter = React.forwardRef<
  HTMLDivElement,
  PaginationCounterProps
>(({ current, total, className }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "absolute bottom-3 left-3 bg-black/50 text-white text-sm font-medium px-3 py-1 rounded-md z-10",
        className
      )}
      aria-live="polite"
      aria-atomic="true"
    >
      {current} / {total}
    </div>
  );
});

PaginationCounter.displayName = "PaginationCounter";

